'use client';

import CustomLink from 'components/shared/CustomLink';
import { Container } from '..';
import { H2, PBody as P } from './styleConstants';
import Image from 'next/image';
import styles from './TestsShowcaseSection.module.css';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useRef } from 'react';
import gsap from 'gsap';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { useSearchParams } from 'next/navigation';
import { getAssetUrl } from 'lib/config/assetUrls';

type TestDataType = {
  icon: string;
  title: string;
  description: string;
  tag: string;
};

const TestCardsData: Array<Array<TestDataType>> = [
  [
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/08.svg'),
      title: 'Peak Performance',
      description:
        'The peak performance blood testing panel is built for a comprehensive and gold...',
      tag: 'Male • 104 Parameters',
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/01.svg'),
      title: 'Advanced',
      description:
        'The advanced panel goes beyond the basics, offering an extensive list of blood...',
      tag: 'Male • 93 Parameters',
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/02.svg'),
      title: 'Foundational',
      description:
        'The foundational panel includes a list of key blood parameters that provide es...',
      tag: 'Male • 72 Parameters',
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/02.svg'),
      title: 'Peak Performance',
      description:
        'The peak performance blood testing panel is built for a comprehensive and gold...',
      tag: 'Female • 102 Parameters',
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/04.svg'),
      title: 'Advanced',
      description:
        'The advanced panel goes beyond the basics, offering an extensive list of blood...',
      tag: 'Female • 95 Parameters',
    },
  ],
  [
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/05.svg'),
      title: 'Foundational',
      description:
        'The foundational panel includes a list of key blood parameters that provide es...',
      tag: 'Female • 74 Parameters',
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/06.svg'),
      title: 'Heart Health',
      description:
        'The cardiovascular health panel is designed to assess key markers related to h...',
      tag: '10 Parameters',
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/07.svg'),
      title: 'Diabetes Risk',
      description:
        'The Diabetes Risk Assessment Panel examines fasting blood sugar, HbA1c and ins...',
      tag: '9 Parameters',
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/11.svg'),
      title: 'Foundational',
      description:
        'The foundational panel includes a list of key blood parameters that provide es...',
      tag: 'Male • 72 Parameters',
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/13.svg'),
      title: 'Advanced',
      description:
        'The advanced panel goes beyond the basics, offering an extensive list of blood...',
      tag: 'Male • 93 Parameters',
    },
  ],
  [
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/09.svg'),
      title: 'Inflammation Status',
      description:
        'This test helps detect inflammation in the body, caused by either injury, infe...',
      tag: '31 Parameters',
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/10.svg'),
      title: 'Sleep Status',
      description:
        'For Sleep health, the relevant markers are the ones that affect your sleep/wak...',
      tag: '35 Parameters',
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/12.svg'),
      title: 'Fatigue',
      description:
        'Our body rely on a wide range of vitamins, minerals, hormones and enzymes that...',
      tag: '46 Parameters',
    },
    {
      icon: getAssetUrl('/web_v2/blood-vision/icons/white/01.svg'),
      title: 'Peak Performance',
      description:
        'The peak performance blood testing panel is built for a comprehensive and gold...',
      tag: 'Male • 104 Parameters',
    },
  ],
];

const TestCards = ({
  title,
  icon,
  description,
  tag,
}: TestDataType) => {
  return (
    <div className={styles.card}>
      <Image src={icon} alt={title} width={48} height={48} />
      <p className={styles.cardTag}>{tag}</p>
      <h5 className={styles.cardTitle}>{title}</h5>
      <p className={styles.cardDescription}>{description}</p>
    </div>
  );
};

export const TestsShowcaseSection = ({
  className,
}: {
  className?: string;
}) => {
  const intersectionObserver = useRef<IntersectionObserver | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    const gsapContext = useGsapContext();

    const { width } = useWindowSize();

    const searchParams = useSearchParams();

    useIsomorphicLayoutEffect(() => {
      if (!rootRef.current || !containerRef.current) return;

      intersectionObserver.current = new IntersectionObserver(
        () => {
          gsapContext.add(() => {
            const tl = gsap.timeline({
              defaults: {
                duration: 1,
              },
              scrollTrigger: {
                immediateRender: false,
                trigger: containerRef.current,
                start: 'top 90%',
                end: 'bottom top',
                scrub: 0,
                invalidateOnRefresh: true,
              },
            });

            tl.fromTo(
              '.col.even',
              { y: 0 },
              { y: width > 768 ? '-80%' : '-30%' },
            );
            tl.fromTo(
              '.col.odd',
              { y: 0 },
              { y: width > 768 ? '100%' : '30%' },
              0,
            );
          });
        },
        {
          root: null,
          rootMargin: '20px',
          threshold: 0.01,
        },
      );

      intersectionObserver.current.observe(rootRef.current);

      return () => {
        intersectionObserver.current?.disconnect();
        // gsapContext.clear();
      };
    }, []);

    return (
      <section
        className={className ? `${styles.root} ${className}` : styles.root}
        ref={rootRef}
      >
        <Container className={styles.content}>
          <H2 className={styles.title}>
            Blood markers mapped to lifestyle, tuned for Longevity.
          </H2>
          <P className={styles.subtitle}>
            Blood Vision is a radical new way to understand and interpret your
            blood markers in the context of lifestyle factors and longevity.
            Blood Vision&apos;s marker ranges are derived from cutting-edge
            research and allow individuals to track the progression of their
            health markers over time.
          </P>
        </Container>
        <div className={styles.ctaContainer}>
          <CustomLink
            href={{
              pathname: '/blood-vision/buy',
              query: Object.fromEntries(searchParams?.entries() ?? []),
            }}
            className="primary"
          >
            Book test
          </CustomLink>
        </div>
        <div className={styles.testListContainer} ref={containerRef}>
          {TestCardsData.map((element, index) => {
            return (
              <div
                key={index}
                className={`col ${(index + 1) % 2 === 0 ? 'even' : 'odd'}`}
              >
                {element.map((childElement, childIndex) => {
                  return <TestCards key={childIndex} {...childElement} />;
                })}
              </div>
            );
          })}
        </div>

        <div className={styles.locationDisclaimer}>
          {/* *We&apos;re currently available in <b>India</b> across{' '}
          <b>Bangalore,</b> <b>Mumbai,</b> <b>Delhi,</b> <b>Hyderabad,</b>{' '}
          <b>Noida,</b> <b>Gurugram</b> and <b>Faridabad</b>. */}
          *We&apos;re currently available in <b>India</b> and <b>US</b>.
          <br />
          <br />
          <b>Available in UK soon.</b>
        </div>
      </section>
    );
};
